import { useState, useEffect, useMemo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { StateYearContext } from './state-year-context';
import { asSessionId, asStateId } from '@/types/v2';
import type { StateId } from '@/types/v2';
import { useCurrentSession } from '@/hooks/v2/useSessionsV2';
import { useStatesV2 } from '@/hooks/v2/useStatesV2';
import { useToast } from '@/hooks/useToast';

export function StateYearProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStateId, setCurrentStateId] = useState<StateId | null>(null);
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  const [isPremium, _setIsPremium] = useState(false); // This would be determined by user subscription
  const [initialized, setInitialized] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // Fetch states via V2 hook
  const { data: statesData, isLoading: statesLoading, error: statesError } = useStatesV2();
  const states = useMemo(() => statesData ?? [], [statesData]);

  // Generate years (current year and 4 years back)
  const years = useMemo(() => {
    const currentYearValue = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYearValue - i);
  }, []);

  // Show toast on states fetch error
  useEffect(() => {
    if (statesError) {
      toast({
        title: 'Error loading states',
        description: 'Please refresh the page to try again.',
        variant: 'destructive',
      });
    }
  }, [statesError, toast]);

  // Fetch current session for V2 hooks
  const { data: currentSession } = useCurrentSession(asStateId(currentStateId || 0));
  const currentSessionId = currentSession?.id ? asSessionId(currentSession.id) : null;

  // Initialize from URL params - only run once
  useEffect(() => {
    if (initialized) return;

    const params = new URLSearchParams(location.search);
    const stateCode = params.get('state');
    const yearParam = params.get('year');

    if (stateCode && states.length > 0) {
      const state = states.find((s) => s.code === stateCode);
      if (state) {
        setCurrentStateId(asStateId(state.id));
      }
    }

    if (yearParam) {
      setCurrentYear(parseInt(yearParam, 10));
    }

    setInitialized(true);
  }, [location.search, initialized, states]);

  // Apply defaults once states have loaded
  useEffect(() => {
    if (defaultsApplied || states.length === 0) return;

    // If no state is selected yet, default to Kansas
    if (!currentStateId) {
      const kansas = states.find((s) => s.code === 'KS');
      const defaultState = kansas ?? states[0];

      setCurrentStateId(asStateId(defaultState.id));

      // Only update URL if we're on the home page and no state is set
      if (location.pathname === '/' || location.pathname === '') {
        const params = new URLSearchParams(location.search);
        if (!params.get('state')) {
          params.set('state', defaultState.code);
          navigate({ search: params.toString() }, { replace: true });
        }
      }
    }

    // If no year is selected yet, default to the current year
    if (!currentYear) {
      const currentYearValue = new Date().getFullYear();
      setCurrentYear(currentYearValue);

      // Only update URL if we're on the home page and no year is set
      if (location.pathname === '/' || location.pathname === '') {
        const params = new URLSearchParams(location.search);
        if (!params.get('year')) {
          params.set('year', currentYearValue.toString());
          navigate({ search: params.toString() }, { replace: true });
        }
      }
    }

    setDefaultsApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]); // Only re-run when states data arrives

  // Update URL when state or year changes - but only if we're on the home page
  useEffect(() => {
    // Skip if we're not on the home page
    if (location.pathname !== '/' && location.pathname !== '') {
      return;
    }

    const params = new URLSearchParams(location.search);
    let updated = false;

    if (currentStateId && states.length > 0) {
      const currentState = states.find((s) => s.id === currentStateId);
      if (currentState) {
        const stateCode = params.get('state');
        if (!stateCode || stateCode !== currentState.code) {
          params.set('state', currentState.code);
          updated = true;
        }
      }
    }

    if (currentYear) {
      const yearParam = params.get('year');
      if (!yearParam || yearParam !== currentYear.toString()) {
        params.set('year', currentYear.toString());
        updated = true;
      }
    }

    if (updated) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [currentStateId, currentYear, location.pathname, location.search, navigate, states]);

  const value = {
    currentStateId,
    currentYear,
    currentSessionId,
    states,
    years,
    setCurrentStateId,
    setCurrentYear,
    isPremium,
    loading: statesLoading,
    error: statesError ? 'Failed to load state data' : null,
  };

  return <StateYearContext.Provider value={value}>{children}</StateYearContext.Provider>;
}
